import { Button, Grid, styled, CircularProgress } from "@mui/material";
import AppModal from "components/AppModal";
import FlexBox from "components/flexbox/FlexBox";
import AppTextField from "components/input-fields/AppTextField";
import { H2, H6 } from "components/Typography";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import apiService from "Network/apiService";
import * as Yup from "yup";
import { useState } from "react"; // Import useState

const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 450,
    minWidth: 200,
    [theme.breakpoints.down(325)]: {
        maxWidth: "100%"
    }
}));

const AddCategoryModal = ({ open, onClose, edit, data }) => {
    const [loading, setLoading] = useState(false); // State for loader visibility

    // Update initialValues to match the API response structure
    const initialValues = {
        category_name: edit ? data?.category_name || "" : "",
        description: edit ? data?.description || "" : "",
        status: edit ? data?.status || "active" : "active"
    };

    const validationSchema = Yup.object().shape({
        category_name: Yup.string().min(3, "Too Short").required("Name is Required!"),
        description: Yup.string(),
        status: Yup.string().required("Status is Required!")    
    });

    // Add enableReinitialize to handle updates to initialValues
    const { values, errors, handleChange, handleSubmit, touched } = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                let response;
                const payload = {
                    category_name: values.category_name,
                    description: values.description,
                    status: values.status
                };

                if (edit) {
                    payload.category_id = data.id;
                    response = await apiService.put(API_ENDPOINTS.PUT_update_business_category, payload);
                } else {
                    response = await apiService.post(API_ENDPOINTS.POST_business_category, payload);
                }

                if (response?.success) {
                    onClose(true);
                    toast.success(`Category ${edit ? "Updated" : "Added"} Successfully`);
                } else {
                    toast.error(response?.message || `Failed to ${edit ? "update" : "add"} category`);
                }
            } catch (error) {
                console.error("API error:", error);
                toast.error("An unexpected error occurred.");
            } finally {
                setLoading(false);
            }
        }
    });

    return (
        <StyledAppModal open={open} handleClose={onClose}>
            <H2 mb={2}>{edit ? "Edit Category" : "Add new Category"}</H2>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <H6 mb={1}>Category Name</H6>
                        <AppTextField
                            fullWidth
                            size="small"
                            name="category_name"
                            placeholder="Category Name"
                            value={values.category_name}
                            onChange={handleChange}
                            error={Boolean(errors.category_name && touched.category_name)}
                            helperText={touched.category_name && errors.category_name}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <H6 mb={1}>Description</H6>
                        <AppTextField
                            fullWidth
                            multiline
                            rows={4}
                            size="small"
                            name="description"
                            placeholder="Category Description"
                            value={values.description}
                            onChange={handleChange}
                            error={Boolean(errors.description && touched.description)}
                            helperText={touched.description && errors.description}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <H6 mb={1}>Status</H6>
                        <AppTextField
                            select
                            fullWidth
                            size="small"
                            name="status"
                            SelectProps={{ native: true }}
                            value={values.status}
                            onChange={handleChange}
                            error={Boolean(errors.status && touched.status)}
                            helperText={touched.status && errors.status}
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </AppTextField>
                    </Grid>
                </Grid>

                <FlexBox justifyContent="flex-end" gap={2} marginTop={4}>
                    <Button fullWidth size="small" variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        type="submit"
                        variant="contained"
                        disabled={loading} // Disable button while loading
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Save"}
                    </Button>
                </FlexBox>
            </form>
        </StyledAppModal>
    );
};

export default AddCategoryModal;
